'use client';

import React, { useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import AppImage from '@/components/ui/AppImage';

interface GuestDashboardLoginProps {
  onLogin: (user: any) => void;
}

export default function GuestDashboardLogin({ onLogin }: GuestDashboardLoginProps) {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const supabase = createClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'signin') {
        const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
        onLogin(data.user);
      } else {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name: name } },
        });
        if (signUpError) throw signUpError;
        if (data.user) onLogin(data.user);
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <AppImage
          src="/assets/images/IMG_7244-1775943860244.jpg"
          alt="Aerial view of Dartmouth harbour with boats and River Dart estuary at dusk"
          fill
          priority
          className="object-cover"
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-deep-navy/60" />
        <div className="absolute inset-0 bg-buttermilk/10" />
      </div>

      {/* Card */}
      <div className="relative z-10 w-full max-w-md mx-auto px-6 py-16">
        <div className="bg-buttermilk/95 backdrop-blur-md border border-border p-10 md:p-12">
          {/* Header */}
          <div className="text-center mb-10">
            <p className="font-serif text-4xl tracking-[0.15em] text-faded-navy mb-3">G & B</p>
            <p className="text-xs tracking-[0.35em] uppercase font-sans text-stone mb-6">
              Guest Dashboard
            </p>
            <div className="h-px bg-border w-12 mx-auto" />
          </div>

          <p className="font-sans text-sm text-stone text-center mb-8 leading-relaxed">
            {mode === 'signin' ?'Sign in to view your RSVP details and weekend schedule.' :'Create an account to access your personal guest dashboard.'}
          </p>

          {error && (
            <div className="mb-6 p-3 border border-red-200 bg-red-50 text-xs font-sans text-red-600">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {mode === 'signup' && (
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
                  Your Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="George Shipman"
                  className="input-field"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className="input-field"
                required
                minLength={6}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 text-xs tracking-[0.3em] uppercase font-sans bg-faded-navy text-buttermilk hover:bg-deep-navy transition-all duration-400 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              style={{ minHeight: '52px' }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {mode === 'signin' ? 'Signing In' : 'Creating Account'}
                </>
              ) : (
                mode === 'signin' ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <button
              onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); }}
              className="text-xs tracking-[0.15em] uppercase font-sans text-stone hover:text-faded-navy transition-colors duration-300 underline underline-offset-4"
            >
              {mode === 'signin' ? 'New here? Create an account' : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-0 right-0 text-center">
        <p className="text-xs tracking-[0.3em] uppercase font-sans text-off-white/40">
          Dartmouth, Devon · XXIX · VIII · MMXXVI
        </p>
      </div>
    </div>
  );
}
